// A SELECT, RADIO or CHECKBOX field's stored content: its options and the chosen answer(s), as JSON.
import { z } from "zod";

import { parseJson } from "@components/FieldComponent/utils";

const emptyArrayToNull = (val: unknown): unknown => (Array.isArray(val) && val.length === 0 ? null : val);

const singleChoiceSchema = z.object({
  options: z.array(z.string()),
  answer: z.preprocess(emptyArrayToNull, z.string().nullish()),
});

const multiChoiceSchema = z.object({
  options: z.array(z.string()),
  answer: z.array(z.string()),
});

const parseContent = <Schema extends z.ZodType>(
  schema: Schema,
  emptyContent: unknown,
  value: string | null | undefined,
) => schema.safeParse(value ? parseJson<unknown>(value) : emptyContent);

export const parseSelectContent = (value: string | null | undefined) =>
  parseContent(singleChoiceSchema, { options: [], answer: null }, value);

export const parseRadioContent = (value: string | null | undefined) =>
  parseContent(singleChoiceSchema, { options: [], answer: "" }, value);

export const parseCheckboxContent = (value: string | null | undefined) =>
  parseContent(multiChoiceSchema, { options: [], answer: [] }, value);

/** The checked options in the order the field lists them, whatever order they were clicked in. */
export const getCheckedOptions = ({ options, answer }: z.infer<typeof multiChoiceSchema>): string[] =>
  options.filter((option) => answer.includes(option));
